import classNames from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import zxcvbn from 'zxcvbn';
import Typography from '../typography';
import { passStrengthCls, PasswordStrengthSC } from './password-strength.styled';

type TProps = {
  password?: string;
  style?: React.CSSProperties;
  className?: string;
};

const PasswordStrength: React.FC<TProps> = ({ password, style, className }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const strength = React.useMemo(() => {
    if (!password || password.length === 0) {
      return null;
    }

    return zxcvbn(password).score;
  }, [password]);

  const params = React.useMemo(() => {
    switch (strength) {
      case 0:
        return {
          color: theme.color.tint.secondary,
          text: 'password.score.weak',
          width: '0%',
        };
      case 1:
        return {
          color: theme.color.solid.error,
          text: 'password.score.weak',
          width: '25%',
        };
      case 2:
        return {
          color: theme.color.solid.warning,
          text: 'password.score.okay',
          width: '50%',
        };
      case 3:
        return {
          color: theme.color.solid.success,
          text: 'password.score.good',
          width: '75%',
        };
      case 4:
        return {
          color: theme.color.tint.success,
          text: 'password.score.strong',
          width: '100%',
        };
      default:
        return {
          color: theme.color.tint.secondary,
          text: 'password.score.no',
          width: '0%',
        };
    }
  }, [strength, theme]);

  return (
    <PasswordStrengthSC className={(passStrengthCls.wrapper, classNames(className))} style={style}>
      <div className={passStrengthCls.track}>
        <div
          className={passStrengthCls.trackItem}
          style={{
            width: params.width,
            background: params.color,
          }}
        />
      </div>
      <div className={passStrengthCls.labelWrapper}>
        <Typography className={passStrengthCls.label} display="body2">
          {formatMessage({ id: 'password.score.label' })}
        </Typography>
        <Typography
          display="body2"
          style={{ color: params.color }}
          className={passStrengthCls.strength}
        >
          {params.text ? formatMessage({ id: params.text }) : null}
        </Typography>
      </div>
    </PasswordStrengthSC>
  );
};

export default PasswordStrength;
